import type { QueueItem } from '@rp/contracts'
import { Chip, DeadlineToken, LedgerRow, LedgerRowContent, type LedgerRowState } from '../../design'
import { formatRelative, replyCountdown, type Countdown } from '../../lib/time'
import { claimExpired } from '../item/ClaimBar'
import { RECLASSIFY_COPY } from '../reclassify/copy'
import { NotSpamButton } from '../reclassify/NotSpamButton'
import { QUEUE_COPY } from './copy'
import {
  CLASSIFICATION_SOURCE_LABELS,
  INTENT_LABELS,
  KIND_LABELS,
  PLATFORM_LABELS,
  SUGGESTION_STATUS_LABELS,
  classificationSourceTone,
  intentTone,
  pageProblemOf,
  suggestionStatusTone,
  suggestionStatusVariant,
} from './labels'
import { activeViewerOf, isUntouched } from './presence'

const percent = (value: number): string => `${Math.round(value * 100)}%`

const rowState = (item: QueueItem, now: Date, currentUserId: string): LedgerRowState => {
  if (item.claim !== null && !claimExpired(item, now)) {
    return item.claim.userId === currentUserId ? 'selected' : 'muted'
  }
  return isUntouched(item) ? 'fresh' : 'default'
}

const hasReason = (item: QueueItem): boolean =>
  item.folder !== 'inbox' &&
  item.classification.status === 'done' &&
  (item.classification.source !== null || item.classification.rule !== null)

const SourceChip = ({ item }: { item: QueueItem }) => {
  const { source } = item.classification
  if (source === null) return null
  return (
    <Chip tone={classificationSourceTone(source)} variant="outline">
      {CLASSIFICATION_SOURCE_LABELS[source]}
    </Chip>
  )
}

const ClaimChip = ({ item, now, currentUserId }: { item: QueueItem; now: Date; currentUserId: string }) => {
  if (item.claim === null || claimExpired(item, now)) return null
  if (item.claim.userId === currentUserId)
    return (
      <Chip tone="success" variant="solid">
        {QUEUE_COPY.claimedByMe}
      </Chip>
    )
  return (
    <Chip tone="neutral" variant="outline">
      {QUEUE_COPY.claimedByOther(item.claim.displayName)}
    </Chip>
  )
}

const ViewerChip = ({ item, now }: { item: QueueItem; now: Date }) => {
  const viewer = activeViewerOf(item, now)
  if (viewer === null) return null
  const claimedByViewer = item.claim !== null && !claimExpired(item, now) && item.claim.userId === viewer.userId
  if (claimedByViewer) return null
  return (
    <Chip tone="neutral" variant="outline">
      {QUEUE_COPY.viewedByOther(viewer.displayName)}
    </Chip>
  )
}

const PageChip = ({ item }: { item: QueueItem }) => {
  const problem = pageProblemOf(item.pageConnection)
  if (problem === null) return null
  return (
    <Chip tone="danger" variant="outline">
      {problem}
    </Chip>
  )
}

const RowChips = ({ item, now, currentUserId }: { item: QueueItem; now: Date; currentUserId: string }) => (
  <>
    {item.classification.status === 'failed' ? (
      <Chip tone="warning" variant="solid">
        {QUEUE_COPY.unclassified}
      </Chip>
    ) : (
      <>
        {item.classification.intent === null ? null : (
          <Chip tone={intentTone(item.classification.intent)}>{INTENT_LABELS[item.classification.intent]}</Chip>
        )}
        {hasReason(item) ? null : <SourceChip item={item} />}
      </>
    )}
    {item.suggestionStatus === null ? null : (
      <Chip tone={suggestionStatusTone(item.suggestionStatus)} variant={suggestionStatusVariant(item.suggestionStatus)}>
        {SUGGESTION_STATUS_LABELS[item.suggestionStatus]}
      </Chip>
    )}
    <ClaimChip item={item} now={now} currentUserId={currentUserId} />
    <ViewerChip item={item} now={now} />
    <PageChip item={item} />
  </>
)

const ReasonBlock = ({ item }: { item: QueueItem }) => {
  if (!hasReason(item)) return null
  const { rule, confidence } = item.classification
  return (
    <div className="queue-reason">
      <SourceChip item={item} />
      {rule === null ? null : <code className="queue-rule">{rule}</code>}
      {item.folder === 'spam' && confidence !== null ? (
        <span className="queue-confidence num">{QUEUE_COPY.confidence(percent(confidence))}</span>
      ) : null}
    </div>
  )
}

const RowAction = ({ item }: { item: QueueItem }) => {
  if (item.folder === 'inbox') return null
  return (
    <div className="queue-row-action">
      <NotSpamButton item={item} label={item.folder === 'spam' ? RECLASSIFY_COPY.notSpam : RECLASSIFY_COPY.unmute} />
    </div>
  )
}

const deadlineMarker = (countdown: Countdown) => <DeadlineToken label={countdown.text} tone={countdown.tone} />

const RowTitle = ({ item, untouched }: { item: QueueItem; untouched: boolean }) => (
  <>
    {item.authorName}
    {untouched ? <span className="visually-hidden">{QUEUE_COPY.notYetViewed}</span> : null}
    {item.unreadCount > 0 ? (
      <>
        <span className="ledger-unread num" aria-hidden="true">
          {item.unreadCount}
        </span>
        <span className="visually-hidden">{QUEUE_COPY.unreadCount(item.unreadCount)}</span>
      </>
    ) : null}
  </>
)

export const QueueItemCard = ({
  item,
  now,
  onOpen,
  currentUserId,
  current = false,
}: {
  item: QueueItem
  now: Date
  onOpen: (id: string) => void
  currentUserId: string
  current?: boolean
}) => {
  const countdown = replyCountdown(item.replyDeadlineAt === null ? null : new Date(item.replyDeadlineAt), now)
  const state = rowState(item, now, currentUserId)

  return (
    <LedgerRow state={state} current={current} unread={item.unreadCount > 0}>
      <div className="ledger-link queue-row">
        <button
          type="button"
          className="queue-open"
          onClick={() => onOpen(item.id)}
          {...(current ? { 'aria-current': 'true' as const } : {})}
          aria-label={QUEUE_COPY.openItem(item.authorName, item.preview)}
        />
        <LedgerRowContent
          {...(countdown === null ? {} : { marker: deadlineMarker(countdown), timeTone: countdown.tone })}
          title={<RowTitle item={item} untouched={state === 'fresh'} />}
          source={`${PLATFORM_LABELS[item.platform]}・${KIND_LABELS[item.kind]}`}
          time={formatRelative(new Date(item.lastMessageAt), now)}
          preview={item.preview}
          chips={<RowChips item={item} now={now} currentUserId={currentUserId} />}
        />
      </div>
      <ReasonBlock item={item} />
      <RowAction item={item} />
    </LedgerRow>
  )
}
